import { useEffect } from "react";
import { Badge, Card, cn, OverflowText, Tooltip } from "ui";
import {
  CheckboxFormField,
  FormField,
  FormLabel,
  SwitchFormField,
  useFormContext,
  useWatch,
} from "../../../features/form";
import { methodIcon } from "../SelectPaymentMethod";
import { snakeCaseToHumanReadable } from "../../../utils/snakeCaseToHumanReadable";
import { InformationCircleIcon } from "icons/outline";
import { CloneableComponents } from "./types";

type CloneStepProps = {
  cloneableComponents?: CloneableComponents;
};

export const CloneStep = ({ cloneableComponents }: CloneStepProps) => {
  if (!cloneableComponents) return null;

  return (
    <div className="space-y-6">
      <RoutingRulesSection routingRules={cloneableComponents.routing_rules} />

      <DynamicCheckoutSection />

      <RiskRulesSection riskRules={cloneableComponents.risk_rules} />

      <BinLookupSection binLookup={cloneableComponents.bin_lookup} />
    </div>
  );
};

const SectionCard = ({
  title,
  children,
  disabled,
}: {
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
}) => {
  return (
    <Card
      disableHover
      className={cn(
        "border-foundations-subtle bg-foundations-subtelst-02 p-4",
        disabled && "cursor-not-allowed"
      )}
    >
      <h4
        className={cn(
          "text-xs font-medium uppercase text-foundations-bold",
          disabled && "text-foundations-subtle"
        )}
      >
        {title}
      </h4>

      {children}
    </Card>
  );
};

const RoutingRulesSection = ({
  routingRules,
}: {
  routingRules: CloneableComponents["routing_rules"];
}) => {
  const disabled = !routingRules.enabled;
  const {
    formState: { errors },
    clearErrors,
  } = useFormContext();
  const routingRulesError = errors.routing_rules?.message as string | undefined;
  const watchedRoutingRules = useWatch({ name: "routing_rules" });

  useEffect(() => {
    if (!watchedRoutingRules || !routingRulesError) return;
    const hasSelected = Object.values(watchedRoutingRules).some(Boolean);
    if (hasSelected) clearErrors("routing_rules");
  }, [watchedRoutingRules, routingRulesError, clearErrors]);

  return (
    <SectionCard title="Routing Rules">
      <div className="mt-4 space-y-3">
        <p className="mb-2 text-sm font-medium text-foundations-bolder">
          Select which rules to clone
        </p>

        {disabled && routingRules.reason && (
          <span className="flex items-center gap-2.5 text-xs text-foundations-subtle">
            <InformationCircleIcon className="h-4 w-4 shrink-0 text-foundations-subtler" />
            {routingRules.reason}
          </span>
        )}

        {routingRules.items.map(
          ({ payment_method, disabled, missing_on_accounts, reason }) => (
            <FormField
              key={payment_method}
              name={`routing_rules.${payment_method}`}
              label={payment_method}
              disabled={disabled}
            >
              <Tooltip disabled={!disabled}>
                <Tooltip.Trigger
                  render={
                    <div>
                      <CheckboxFormField
                        className="flex-row-reverse items-center justify-between space-x-0"
                        customLabel={
                          <div className="flex items-center justify-end gap-1.5 py-2">
                            {methodIcon[payment_method]}
                            <span className="text-sm capitalize text-foundations-bolder">
                              {snakeCaseToHumanReadable(
                                payment_method.toLowerCase()
                              )}
                            </span>
                          </div>
                        }
                      />
                    </div>
                  }
                ></Tooltip.Trigger>
                <Tooltip.Popup size="sm" variant="light">
                  {reason ||
                    `Routing rule is missing on the target account ${missing_on_accounts
                      .map((a) => a.name)
                      .join(", ")}`}
                </Tooltip.Popup>
              </Tooltip>
            </FormField>
          )
        )}

        {routingRulesError && (
          <p className="text-sm text-danger">{routingRulesError}</p>
        )}
      </div>
    </SectionCard>
  );
};

const DynamicCheckoutSection = () => {
  return (
    <SectionCard title="Dynamic Checkout" disabled>
      <span className="mt-4 flex items-center gap-1.5 text-sm text-foundations-subtle">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-foundations-subtlest">
          <InformationCircleIcon
            className="h-4 w-4 !text-foundations-subtler"
            aria-hidden="true"
          />
        </div>
        Please configure the dynamic checkout in your new flow.
      </span>
    </SectionCard>
  );
};

const RiskRulesSection = ({
  riskRules,
}: {
  riskRules: CloneableComponents["risk_rules"];
}) => {
  const disabled = !riskRules.enabled;
  const riskRulesEnabled = useWatch({ name: "risk_rules_enabled" });

  return (
    <SectionCard title="Risk Rules" disabled={disabled}>
      <Tooltip disabled={!disabled}>
        <Tooltip.Trigger
          render={
            <div className="mt-4 flex items-center justify-between gap-2">
              <span
                className={cn(
                  "text-sm font-medium text-foundations-bolder",
                  disabled && "text-foundations-subtle"
                )}
              >
                Clone risk rules
              </span>

              <FormField
                name="risk_rules_enabled"
                label="Risk rules enabled"
                disabled={disabled}
              >
                <FormLabel className="sr-only" />
                <SwitchFormField size="sm" />
              </FormField>
            </div>
          }
        ></Tooltip.Trigger>
        <Tooltip.Popup size="sm" variant="light" className="max-w-sm">
          {riskRules.reason}
        </Tooltip.Popup>
      </Tooltip>

      {riskRulesEnabled && (
        <>
          <span className="font-mono text-[10px] uppercase text-foundations-bold">
            Risk rules
          </span>

          <ul className="mt-2 flex flex-wrap items-center gap-2">
            {riskRules.items.map((rule) => (
              <li key={rule.id}>
                <Badge
                  shape="square"
                  className="bg-foundations-subtelst-02 px-2 py-1 text-xs font-medium text-foundations-bold"
                >
                  {rule.name}
                </Badge>
              </li>
            ))}
          </ul>
        </>
      )}
    </SectionCard>
  );
};

const BinLookupSection = ({
  binLookup,
}: {
  binLookup: CloneableComponents["bin_lookup"];
}) => {
  const disabled = !binLookup.enabled;
  const binLookupEnabled = useWatch({ name: "bin_lookup_enabled" });

  return (
    <SectionCard title="BIN Lookup" disabled={disabled}>
      <Tooltip disabled={!disabled}>
        <Tooltip.Trigger
          render={
            <div className="mt-4 flex items-center justify-between gap-2">
              <span
                className={cn(
                  "text-sm font-medium text-foundations-bolder",
                  disabled && "text-foundations-subtle"
                )}
              >
                Clone BIN lookup configuration
              </span>

              <FormField
                name="bin_lookup_enabled"
                label="BIN lookup enabled"
                disabled={disabled}
              >
                <FormLabel className="sr-only" />
                <SwitchFormField size="sm" />
              </FormField>
            </div>
          }
        ></Tooltip.Trigger>
        <Tooltip.Popup size="sm" variant="light" className="max-w-sm">
          {binLookup.reason}
        </Tooltip.Popup>
      </Tooltip>

      {binLookupEnabled && (
        <>
          <span className="font-mono text-[10px] uppercase text-foundations-bold">
            BIN Lookup provider
          </span>

          <ul className="mt-2 flex flex-wrap items-center gap-2">
            {binLookup.items.map((item) => (
              <li key={item.service_id}>
                <Badge
                  shape="square"
                  className="flex max-w-60 items-center gap-1.5 bg-foundations-subtelst-02 px-2 py-1 text-xs font-medium text-foundations-bold"
                >
                  <img
                    src={item.service_logo}
                    alt={item.service_name}
                    width={24}
                    height={24}
                    className="rounded object-contain"
                  />
                  <OverflowText>{item.service_name}</OverflowText>
                </Badge>
              </li>
            ))}
          </ul>
        </>
      )}
    </SectionCard>
  );
};
